import { Box, Stack, Text, Title } from '@inflearn/ds-react-next';
import { KoOnly } from '@inflearn/i18n-tools';
import { useTranslation } from 'next-i18next';

import { SettingSwitch } from '../SettingsPageTabs/SettingSwitch';
import { MarketingChannelSetting } from './MarketingChannelSetting';
import { Type } from './constants';

type Props = {
  updateNotificationStatus: (type: Type, status: boolean) => Promise<void>;
  notificationStatus: {
    [Type.COMMUNITY_COMMENT]: boolean;
    [Type.COURSE_NEW_NEWS]: boolean;
    [Type.SERVICE_ANNOUNCEMENT]: boolean;
    [Type.MARKETING_EMAIL]: boolean;
    [Type.MARKETING_KAKAO]: boolean;
    [Type.MARKETING_SMS]: boolean;
    [Type.STUDY_SUMMARY]: boolean;
  };
  loading: boolean;
};

export default function NotificationSetting({
  updateNotificationStatus,
  notificationStatus,
  loading,
}: Props) {
  const { t } = useTranslation();

  return (
    <Box css={{ maxWidth: '922px', margin: 'auto' }}>
      <Title order={2} size="h4" mb={4}>
        {t('알림 설정')}
      </Title>
      <Text fz={12} fw={500} c="gray" mb={20}>
        {t(
          '이메일 수신 여부를 설정할 수 있어요. 회원약관 변경, 결제내역 등 필수적으로 안내되어야 하는 내용은 수신여부와 상관 없이 계속 발송됩니다.',
        )}
      </Text>
      <Stack gap="md">
        <KoOnly>
          <SettingSwitch
            title={t('공지사항')}
            description={t(
              '중요한 공지사항, 기능 업데이트 등 새로운 인프런 소식을 받을 수 있어요.',
            )}
            onChange={(checked) => {
              void updateNotificationStatus(Type.SERVICE_ANNOUNCEMENT, checked);
            }}
            checked={notificationStatus.SERVICE_ANNOUNCEMENT}
            loading={loading}
          />
        </KoOnly>
        <SettingSwitch
          title={t('강의 새소식')}
          description={t(
            '수강 중인 강의의 지식공유자가 보내는 강의 새소식 알림을 받을 수 있어요.\n강의별 새소식 설정은 강의 대시보드에서 할 수 있어요.',
          )}
          onChange={(checked) => {
            void updateNotificationStatus(Type.COURSE_NEW_NEWS, checked);
          }}
          checked={notificationStatus.COURSE_NEW_NEWS}
          loading={loading}
        />
        <SettingSwitch
          title={t('커뮤니티 댓글')}
          description={t('작성한 게시글의 댓글, 대댓글이 등록될 때 알림을 받을 수 있어요.')}
          onChange={(checked) => {
            void updateNotificationStatus(Type.COMMUNITY_COMMENT, checked);
          }}
          checked={notificationStatus.COMMUNITY_COMMENT}
          loading={loading}
        />
        <KoOnly>
          <SettingSwitch
            title="복습노트"
            description="어제 학습한 내용을 학습 에이전트가 노트로 정리해 이메일로 보내드려요."
            onChange={(checked) => {
              void updateNotificationStatus(Type.STUDY_SUMMARY, checked);
            }}
            checked={notificationStatus[Type.STUDY_SUMMARY]}
            loading={loading}
          />
        </KoOnly>
        <KoOnly>
          <MarketingChannelSetting
            statuses={{
              [Type.MARKETING_EMAIL]: notificationStatus[Type.MARKETING_EMAIL],
              [Type.MARKETING_KAKAO]: notificationStatus[Type.MARKETING_KAKAO],
              [Type.MARKETING_SMS]: notificationStatus[Type.MARKETING_SMS],
            }}
            onChange={(type, checked) => {
              void updateNotificationStatus(type, checked);
            }}
            loading={loading}
          />
        </KoOnly>
      </Stack>
    </Box>
  );
}
